/**
 * Deep sanitation helper that strips any `undefined` field values from objects and arrays.
 * Firestore DocumentReference.set() / update() rejects any document containing `undefined`.
 */
export const stripUndefinedDeep = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value
      .filter((v) => v !== undefined)
      .map((v) => stripUndefinedDeep(v)) as unknown as T
  }
  if (value && typeof value === 'object') {
    if (value instanceof Date) return value
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue
      out[k] = stripUndefinedDeep(v)
    }
    return out as unknown as T
  }
  return value
}

/**
 * Traverses an object and returns the property paths of any fields having `undefined` values.
 */
export const findUndefinedPaths = (obj: unknown, prefix = ''): string[] => {
  const paths: string[] = []
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      paths.push(...findUndefinedPaths(item, `${prefix}[${index}]`))
    })
  } else if (obj && typeof obj === 'object') {
    if (obj instanceof Date) return paths
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = prefix ? `${prefix}.${key}` : key
      if (value === undefined) {
        paths.push(currentPath)
      } else if (typeof value === 'object' && value !== null) {
        paths.push(...findUndefinedPaths(value, currentPath))
      }
    }
  }
  return paths
}
